import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import ReportService, { downloadFile } from '../../api/TransfertTitre/ReportService';
import useSelectedDomain from '../useSelectedDomain';

export const useReportGeneration = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentReport, setCurrentReport] = useState(null);
  const selectedDomain = useSelectedDomain();

  const generateReport = useCallback(async (reportType, securityId, options = {}) => {

    if (!selectedDomain?.id) {
      const errorMsg = 'Veuillez sélectionner un domaine';
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }

    if (!securityId) {
      const errorMsg = 'ID du titre manquant';
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }

    setLoading(true);
    setError(null);
    setCurrentReport(reportType);

    try {
      let reportBlob;
      let filename;
      const domainId = selectedDomain.id;

      const reportName = getReportName(reportType);
      const format = getReportFormat(reportType);

      switch (reportType) {
        // ── Rapports standards ────────────────────────────────────────────────

        case 'shareholders-list':
          reportBlob = await ReportService.generateShareholdersList(
            domainId,
            securityId,
            options.includeZeroBalance || false
          );
          filename = `Liste_Actionnaires_${Date.now()}.pdf`;
          break;

        case 'accounting-plan':
          reportBlob = await ReportService.generateAccountingPlan(
            domainId,
            securityId
          );
          filename = `Plan_Comptable_${Date.now()}.pdf`;
          break;

        case 'account-history':
          if (!options.startDate || !options.endDate) {
            const errorMsg = "L'historique des comptes nécessite une période (date de début et fin)";
            toast.error(errorMsg);
            throw new Error(errorMsg);
          }
          reportBlob = await ReportService.generateAccountHistory(
            domainId,
            securityId,
            options.startDate,
            options.endDate
          );
          filename = `Historique_Comptes_${Date.now()}.zip`;
          break;

        case 'emission-journal':
          reportBlob = await ReportService.generateEmissionJournal(
            domainId,
            securityId,
            options.startDate,
            options.endDate
          );
          filename = `Journal_Emission_${Date.now()}.pdf`;
          break;

        // ── Marché privé ──────────────────────────────────────────────────────

        case 'attestation-propriete':
          reportBlob = await ReportService.generateAttestationPropriete(domainId, securityId);
          filename = `Attestations_${Date.now()}.zip`;
          break;

        case 'compte-conservation':
          reportBlob = await ReportService.generateCompteConservation(domainId, securityId);
          filename = `Compte_Conservation_${Date.now()}.zip`;
          break;

        case 'registre-conservation':
          reportBlob = await ReportService.generateRegistreConservation(domainId, securityId);
          filename = `Registre_Conservation_${Date.now()}.zip`;
          break;

        case 'operation-comptabilite':
          reportBlob = await ReportService.generateOperationComptabilite(
            domainId,
            securityId,
            options.periode || 3
          );
          filename = `Operation_Comptabilite_${Date.now()}.pdf`;
          break;

        case 'compte-global':
          reportBlob = await ReportService.generateCompteGlobal(
            domainId,
            securityId
          );
          filename = `Compte_Global_Emission_${Date.now()}.pdf`;
          break;

        // ── Nouveaux rapports ─────────────────────────────────────────────────

        case 'historique-valeur-nominal':
          reportBlob = await ReportService.generateHistoriqueValeurNominal(
            domainId,
            securityId
          );
          filename = `Historique_Valeur_Nominal_${Date.now()}.pdf`;
          break;

        case 'restriction-titres':
          reportBlob = await ReportService.generateRestrictionTitres(
            domainId,
            securityId
          );
          filename = `Restriction_Titres_${Date.now()}.zip`;
          break;

        case 'tableau-bord-action':
          reportBlob = await ReportService.generateTableauBordAction(
            domainId,
            securityId
          );
          filename = `Tableau_Bord_Action_${Date.now()}.zip`;
          break;

        default:
          throw new Error(`Type de rapport non supporté: ${reportType}`);
      }

      // Vérifier si le blob est valide
      if (!reportBlob || reportBlob.size === 0) {
        const errorMsg = 'Le fichier généré est vide';
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }

      // Télécharger le fichier
      await downloadFile(reportBlob, filename);

      toast.success(`${reportName} généré avec succès`, {
        description: `Le fichier ${filename} a été téléchargé`,
        duration: 5000
      });

      return { success: true, filename, format };
    } catch (err) {
      let errorMessage = err.message || `Erreur lors de la génération du rapport`;

      if (err.message.includes('Paramètres invalides')) {
        errorMessage = 'Les paramètres envoyés au serveur sont incorrects. Vérifiez les données.';
      } else if (err.message.includes('Aucune donnée trouvée')) {
        errorMessage = 'Aucune donnée disponible pour générer ce rapport.';
      } else if (err.message.includes('Erreur interne du serveur')) {
        errorMessage = 'Erreur interne du serveur. Veuillez contacter l\'administrateur.';
      }

      setError(errorMessage);

      toast.error(errorMessage, {
        duration: 7000,
        description: 'Veuillez vérifier les paramètres et réessayer'
      });

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
      setTimeout(() => setCurrentReport(null), 500);
    }
  }, [selectedDomain]);

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const getReportName = (reportType) => {
    const names = {
      'shareholders-list':        'Liste des actionnaires',
      'accounting-plan':          'Plan comptable',
      'account-history':          'Historique des comptes',
      'emission-journal':         "Journal auxiliaire d'émission",
      'attestation-propriete':    'Attestation de propriété',
      'compte-conservation':      'Compte de conservation',
      'registre-conservation':    'Registre de conservation',
      'operation-comptabilite':   'Opération comptabilité',
      'compte-global':            "Compte global d'émission",
      'historique-valeur-nominal':'Historique valeur nominale',
      'restriction-titres':       'Restriction des titres',
      'tableau-bord-action':      'Tableau de bord action',
    };
    return names[reportType] || 'Rapport';
  };

  const getReportFormat = (reportType) => {
    const zipReports = [
      'account-history',
      'attestation-propriete',
      'compte-conservation',
      'registre-conservation',
      'restriction-titres',
      'tableau-bord-action',
    ];
    return zipReports.includes(reportType) ? 'ZIP' : 'PDF';
  };

  const getReportDescription = (reportType) => {
    const descriptions = {
      'shareholders-list':         'Liste complète des actionnaires du titre avec leurs participations',
      'accounting-plan':           'Plan comptable détaillé du titre',
      'account-history':           'Historique des mouvements de comptes par client (ZIP)',
      'emission-journal':          "Journal des opérations d'émission du titre",
      'attestation-propriete':     'Attestations de propriété par client (ZIP)',
      'compte-conservation':       'Comptes de conservation des valeurs mobilières (ZIP)',
      'registre-conservation':     'Registre de conservation des titres (ZIP)',
      'operation-comptabilite':    'Journal des opérations comptables (AUSCAGIE)',
      'compte-global':             "État du compte global d'émission",
      'historique-valeur-nominal': 'Historique de l\'évolution de la valeur nominale du titre',
      'restriction-titres':        'Document de restriction des titres par représentant légal (ZIP)',
      'tableau-bord-action':       'Tableau de bord synthétique des actions par représentant (ZIP)',
    };
    return descriptions[reportType] || '';
  };

  const getReportRequirements = (reportType) => {
    const requirements = {
      'account-history':         'Nécessite une période (dates de début et fin)',
      'emission-journal':        'Dates optionnelles pour filtrer par période',
      'operation-comptabilite':  null, // Seulement periode optionnelle
    };
    return requirements[reportType] || null;
  };

  return {
    loading,
    error,
    currentReport,
    generateReport,
    getReportName,
    getReportFormat,
    getReportDescription,
    getReportRequirements
  };
};